import { GET_WORKSPACE_LAYOUT, PUT_WORKSPACE_LAYOUT } from "../../../api/workspaceLayout";
import { sanitizePayoutCurve, sanitizePieceLayout } from "../../../components/CustomWidget/payoutCurve";
import { sanitizeBonus } from "../../../components/CustomWidget/bonusSteps";
import { sanitizeNetBonus } from "../../../components/NetProfitBonus/netBonus";
import { sanitizeMetricPlan, metricsFromPlan } from "../../../helpers/metricPlan";
import { sanitizeProgram, sanitizeCombinedPay } from "../../../components/SalesBonus/moneyRules";
import { isAllowedMetricColor } from "../../../helpers/metricColor";
import { normalizeLookAhead } from "../../../components/GoalProgress/lookAhead";
import { normalizeYetiTeams, normalizeYetiGoal, normalizeYetiGoals } from "../../../helpers/beatYeti";

/**
 * Per-workspace dashboard layout persistence.
 *
 * Server-first with a localStorage fallback (the same resilience pattern as
 * CompanyMetrics' dashboardStore): the published layout is ALWAYS mirrored to
 * localStorage, so the feature works before the /api/workspace-layout routes
 * are deployed, and survives offline. A layout is a plain array of
 * { i, x, y, w, h } blocks in 12-column react-grid-layout units.
 */

// v2: row units changed when GRID_MARGIN went 16 → 32 (restoring the original
// spacing={4} rhythm). v3: hero and leaderboard split into greeting/mywidgets
// and list/podium blocks. v4: grid resolution doubled to 24 columns so widths
// can land on half-column boundaries. Older saves are ignored.
const VERSION = 4;

/**
 * Widgets that became SCENES of another component. A board saved before the
 * merge still names them, and conformLayout drops ids it does not recognise —
 * so without this, publishing after an update would silently delete the card.
 * Migrating instead of discarding is also why VERSION does not move: bumping it
 * makes the loader reject the whole save, which would reset every customised
 * board in the field.
 */
const MERGED = {
  "kotm-photo": { i: "kotm", scene: "photo" },
  milestones2: { i: "milestones", scene: "track" },
};

/** Per-block settings the editor writes. Anything not listed here is dropped. */
export const sanitizeSettings = (s) => {
  if (!s || typeof s !== "object") return undefined;
  const out = {};
  if (typeof s.title === "string" && s.title.trim()) out.title = s.title.trim().slice(0, 60);
  if (typeof s.scene === "string" && s.scene) out.scene = s.scene;
  if (typeof s.period === "string" && s.period) out.period = s.period;
  /* "Build your own" widgets: up to two metric rows (each with its own graph),
     an optional bonus rule, and the chosen layout. The single metricId/graph
     pair is still accepted so widgets built before two-metric support load. */
  const GRAPHS = ["number", "target", "ring", "donut", "gauge", "bar", "area", "line", "spark"];
  const row = (r) => {
    if (!r || typeof r !== "object" || typeof r.metricId !== "string" || !r.metricId) return null;
    const clean = { metricId: r.metricId, graph: GRAPHS.includes(r.graph) ? r.graph : "number" };
    // Hex accents plus Appearance sentinels ("company" / "none" / "goal"). Those used
    // to be stripped here, so picking Company colour looked saved until reload.
    if (isAllowedMetricColor(r.color)) clean.color = r.color;
    return clean;
  };
  if (Array.isArray(s.metrics)) {
    /* Cap must match MAX_METRICS in widgetRegistry (keep in sync — avoid
       importing the registry here; it pulls half the app). */
    const rows = s.metrics.map(row).filter(Boolean).slice(0, 6);
    if (rows.length) out.metrics = rows;
  } else if (typeof s.metricId === "string" && s.metricId) {
    out.metrics = [{ metricId: s.metricId, graph: GRAPHS.includes(s.graph) ? s.graph : "number" }];
  }
  /* A commission ({ pct }) or a step ladder ({ type: "steps", steps, per }) —
     one sanitizer for both (bonusSteps.js), mirrored on the server. */
  const bonus = sanitizeBonus(s.bonus);
  if (bonus) out.bonus = bonus;
  if (s.payoutCurve) {
    const pc = sanitizePayoutCurve(s.payoutCurve);
    if (pc) out.payoutCurve = pc;
  }
  /* Optional hard target for Build-your-own + payout % tiers — overrides the
     Scorekeeper KPI monthly target (× period). A quarterly revenue bonus needs
     this when KPI targets are $2–8k and the real gate is $300k. */
  const goal = Number(s.goalTarget);
  if (Number.isFinite(goal) && goal > 0) out.goalTarget = Math.round(goal);
  /* Technicians' Revenue per Hour's goal in $/hr (Build-your-own). Absent = the
     goal the company's own anchor metric carries. */
  const rphGoal = Number(s.rphGoal);
  if (Number.isFinite(rphGoal) && rphGoal > 0) out.rphGoal = Math.round(rphGoal * 100) / 100;
  if (s.pieceLayout) {
    const pl = sanitizePieceLayout(s.pieceLayout);
    if (pl) out.pieceLayout = pl;
  }
  if (typeof s.look === "string" && ["tiles", "meter", "focus", "duo"].includes(s.look)) out.look = s.look;
  /* Goal Progress: the trend line and the goal reference line each get their
     own colour (Appearance tab) — hex only, same rule a metric row's own
     colour already follows. */
  if (isAllowedMetricColor(s.lineColor)) out.lineColor = s.lineColor;
  if (isAllowedMetricColor(s.goalColor)) out.goalColor = s.goalColor;
  /* Revenue, Expenses & Labor: its own per-line colours (Appearance) — same
     hex-only rule as the pair above, one entry per series. */
  if (s.seriesColors && typeof s.seriesColors === "object") {
    const colors = {};
    /* Every id in SERIES (components/ProfitTrend/TrendChart.jsx) — KEEP IN SYNC.
       Not imported: that module pulls recharts, and this one is loaded by the
       store. A series missing from this list has a colour picker in Appearance
       that works all the while the drawer is open (it reads the live draft) and
       is thrown away the instant the board is published — which is exactly what
       happened to `pace` and `loss` when they were added to SERIES and not to
       here. */
    ["revenue", "expenses", "labor", "net", "pace", "loss"].forEach((key) => {
      if (isAllowedMetricColor(s.seriesColors[key])) colors[key] = s.seriesColors[key];
    });
    if (Object.keys(colors).length) out.seriesColors = colors;
  }
  if (GRAPHS.includes(s.chartStyle)) out.chartStyle = s.chartStyle;
  /* Goal Progress's trend view + goal-line mode. Only the NON-default is
     stored, so an untouched card carries neither key and keeps reading the
     running total against a working-day pace. */
  if (s.chartView === "daily") out.chartView = "daily"; // "cumulative" is the default
  if (s.goalLineMode === "flat") out.goalLineMode = "flat"; // "pace" is the default
  if (s.chartDays === "working") out.chartDays = "working"; // "all" is the default
  /* All Department Metrics: bar tiles instead of the climb. */
  if (s.tileStyle === "bar") out.tileStyle = "bar"; // "climb" is the default
  /* Total Scheduled Revenue's look-ahead (extra days past the period). Absent = off. */
  const lookAhead = normalizeLookAhead(s.lookAhead);
  if (lookAhead) out.lookAhead = lookAhead;
  /* Beat the Yeti rate's per-team "7/10" rows: workspace ids, in the order
     picked. Absent = no rows. */
  const yetiTeams = normalizeYetiTeams(s.yetiTeams);
  if (yetiTeams.length) out.yetiTeams = yetiTeams;
  /* The newer switches: rows follow the tracked workspaces, an overall bar, and
     a goal notch at a whole percent. Only the on-states are stored. */
  if (s.yetiByTeam === true) out.yetiByTeam = true;
  if (s.yetiBar === true) out.yetiBar = true;
  const yetiGoal = normalizeYetiGoal(s.yetiGoal);
  if (yetiGoal) out.yetiGoal = yetiGoal;
  const yetiGoals = normalizeYetiGoals(s.yetiGoals);
  if (Object.keys(yetiGoals).length) out.yetiGoals = yetiGoals;
  /* The gap between Goal Progress's Y-axis labels, in the metric's units.
     Absent is Auto (round steps worked out from the numbers). */
  const yStep = Number(s.yStep);
  if (s.yStep != null && s.yStep !== "" && Number.isFinite(yStep) && yStep > 0) out.yStep = yStep;
  /* ── scope · window · average ───────────────────────────────────────────
     Which workspaces a card reads, over which dates, averaged or totalled.
     Missing from here, every one of these was silently dropped on publish:
     the card worked while the editor was open (it reads the live draft) and
     reverted the moment it was saved. `avgMode` is deliberately NOT called
     `show` — that key already exists above with values ranking|activity. */
  if (Array.isArray(s.workspaceIds)) {
    const ids = s.workspaceIds
      .filter((id) => typeof id === "string" && id)
      .filter((id, i, all) => all.indexOf(id) === i)
      .slice(0, 24);
    if (ids.length) out.workspaceIds = ids;
  }
  /* All Department Metrics: which of each row's metrics are hand-picked, how
     many cells a row gets, and whether sub-workspaces come along automatically
     or are picked one at a time. NONE of these four were ever whitelisted
     here, so a Setup edit looked right with the drawer open (it reads the
     live draft, never this sanitized copy) and reverted the instant it
     published — a hand-picked metric list fell back to Automatic, "Metrics
     per row" fell back to 5, and every parent workspace's sub-workspaces came
     back on, no matter what Setup's own tree picker showed. */
  if (s.metricsByWs && typeof s.metricsByWs === "object") {
    const map = {};
    Object.entries(s.metricsByWs).forEach(([wsId, ids]) => {
      if (typeof wsId !== "string" || !wsId || !Array.isArray(ids)) return;
      const clean = ids.filter((id) => typeof id === "string" && id).slice(0, 24);
      map[wsId.slice(0, 40)] = clean;
    });
    if (Object.keys(map).length) out.metricsByWs = map;
  }
  const maxCols = Number(s.maxCols);
  if (Number.isFinite(maxCols) && maxCols > 0) out.maxCols = Math.min(24, Math.round(maxCols));
  if (s.includeSubWorkspaces === false) out.includeSubWorkspaces = false; // true is the default
  if (s.subsExplicit === true) out.subsExplicit = true; // false (legacy auto-expand) is the default
  if (s.filterBy === "player") out.filterBy = "player"; // workspace is the default
  /* Which metrics combine across the workspaces (auto by name, or paired by
     hand with a per-column sum/avg). Dropped here = reverts to name-match. */
  if (s.metricPlan) {
    const mp = sanitizeMetricPlan(s.metricPlan);
    if (mp) {
      out.metricPlan = mp;
      const synced = metricsFromPlan(mp);
      if (synced) out.metrics = synced;
    }
  }
  /* ── Leaderboard: cross-workspace + ranking + rows + TV rotation ─────────
     These were NOT whitelisted, so switching a leaderboard to cross-workspace
     (`across`) or changing its ranking/rows/rotation "saved" while the editor
     was open (it reads the live draft) and reverted the instant it published.
     Persist them so the choice survives publish/reload. */
  if (s.across === true) out.across = true;
  if (typeof s.rankBy === "string" && s.rankBy) out.rankBy = s.rankBy.slice(0, 40);
  if (s.rankOrder === "asc") out.rankOrder = "asc"; // desc is the default
  if (typeof s.tieBreak === "string" && s.tieBreak) out.tieBreak = s.tieBreak.slice(0, 40);
  /* Qualify gate: beat Yeti on N metrics (default 4 of whatever columns exist). */
  if (s.yetiQualify === true) out.yetiQualify = true;
  if (s.yetiQualify === false) out.yetiQualify = false;
  const yqNeed = Number(s.yetiQualifyNeed);
  if (Number.isFinite(yqNeed) && yqNeed > 0) out.yetiQualifyNeed = Math.min(12, Math.round(yqNeed));
  if (s.whoAppears === "active") out.whoAppears = "active"; // "all" is the default
  /* Leaderboard's "rows to show" is a count; Labor's `rows` is an array (one
     department per entry) — same key, two widgets, told apart by shape. Labor
     was not handled here at all, so Setup looked saved with the editor open
     (it reads the live draft) and reverted to empty on the next publish. */
  if (Array.isArray(s.rows)) {
    const laborRow = (r) => {
      if (!r || typeof r !== "object") return null;
      const out2 = {};
      if (typeof r.key === "string" && r.key) out2.key = r.key.slice(0, 40);
      if (typeof r.workspaceId === "string" && r.workspaceId) out2.workspaceId = r.workspaceId;
      /* Two-or-more-workspaces-combined-into-one-row and its sub-workspace
         toggle — BOTH were missing from this whitelist, so a combined row
         silently fell back to just its first workspace, and "Include
         sub-workspaces" silently reverted to on, the instant either one hit
         Setup's autosave. Looked right while the drawer was open (it reads
         the live draft, never this sanitized copy) and wrong again on the
         next reload — this is that bug. */
      if (Array.isArray(r.workspaceIds)) {
        const ids = r.workspaceIds.filter((id) => typeof id === "string" && id).slice(0, 12);
        if (ids.length) out2.workspaceIds = ids;
      }
      if (typeof r.label === "string" && r.label.trim()) out2.label = r.label.trim().slice(0, 60);
      if (r.includeSubWorkspaces === false) out2.includeSubWorkspaces = false; // true is the default
      if (typeof r.hoursKpiId === "string" && r.hoursKpiId) out2.hoursKpiId = r.hoursKpiId;
      const hours = Number(r.hours);
      if (Number.isFinite(hours) && hours >= 0) out2.hours = hours;
      const totalCost = Number(r.totalCost);
      if (Number.isFinite(totalCost) && totalCost >= 0) out2.totalCost = totalCost;
      const goalPct = Number(r.goalPct);
      if (Number.isFinite(goalPct) && goalPct >= 0) out2.goalPct = goalPct;
      /* Per-employee pay rates, keyed by userId — typed on the card itself
         (click a Pay/hour cell), not in this Setup form. The department's own
         Pay/hour is the average of these, not total cost ÷ hours. A plain
         number is an hourly rate (the original, still-supported shape); an
         object is a monthly-salary rate — the card converts it to an
         hourly-equivalent using that month's own weekday count. */
      if (r.employeePay && typeof r.employeePay === "object") {
        const pay = {};
        Object.entries(r.employeePay).forEach(([uid, v]) => {
          if (typeof uid !== "string" || !uid) return;
          const key = uid.slice(0, 40);
          if (v && typeof v === "object") {
            const val = Number(v.value);
            if (v.mode === "monthly" && Number.isFinite(val) && val >= 0) pay[key] = { mode: "monthly", value: val };
            return;
          }
          const n = Number(v);
          if (Number.isFinite(n) && n >= 0) pay[key] = n;
        });
        if (Object.keys(pay).length) out2.employeePay = pay;
      }
      return Object.keys(out2).length ? out2 : null;
    };
    const laborRows = s.rows.map(laborRow).filter(Boolean).slice(0, 24);
    if (laborRows.length) out.rows = laborRows;
  } else {
    const rowsN = Number(s.rows);
    if (Number.isFinite(rowsN) && rowsN > 0) out.rows = Math.min(50, Math.round(rowsN));
  }
  /* Payroll vs Revenue: what the company made this period, typed once — and,
     when a live Total Revenue metric exists, where Setup's self-heal last
     pointed it (revenueKpiOf in components/LaborMetrics/data.js). These two
     were never whitelisted here, so every save silently stripped them from
     what reached the server — invisible within a session (Setup's self-heal
     effect keeps re-deriving them into local state the moment the drawer is
     open, so the card kept reading a correct in-memory value), but a reload
     always came back to the typed-$0 fallback because the SAVED copy never
     actually had them. */
  const laborRevenue = Number(s.revenue);
  if (Number.isFinite(laborRevenue) && laborRevenue >= 0) out.revenue = laborRevenue;
  if (typeof s.revenueWorkspaceId === "string" && s.revenueWorkspaceId) out.revenueWorkspaceId = s.revenueWorkspaceId;
  if (typeof s.revenueKpiId === "string" && s.revenueKpiId) out.revenueKpiId = s.revenueKpiId;
  /* Revenue, Expenses & Labor: how each of the three is sourced, the typed
     expense rows, the per-series goals, which lines are plotted and how the
     chart itself is drawn. NONE of these were whitelisted here, so Setup's
     Expenses rows (and every other field below) looked saved while the
     drawer was open (it reads the live draft, never this sanitized copy) and
     were silently dropped the instant the board published — same bug as
     Payroll vs Revenue's revenue/revenueKpiId above, just never carried
     over to this card. */
  if (["auto", "pick", "manual"].includes(s.revenueMode)) out.revenueMode = s.revenueMode;
  if (s.laborMode === "manual") out.laborMode = "manual"; // "widget" is the default
  if (s.laborBase === "zero") out.laborBase = "zero"; // "expenses" is the default
  if (typeof s.laborWidgetId === "string" && s.laborWidgetId) out.laborWidgetId = s.laborWidgetId;
  /* Net Profit Bonus: the ladder it pays on and which Revenue, Expenses & Labor
     card it reads. The ladder goes through its own sanitizer (netBonus.js), the
     same one the card renders from, so what is saved is exactly what is shown.
     Without these the Setup tab looks saved while the drawer is open and the
     ladder is gone on the next reload — the whitelist trap this file is for. */
  const netBonus = sanitizeNetBonus(s.netBonus);
  if (netBonus) out.netBonus = netBonus;
  if (typeof s.profitWidgetId === "string" && s.profitWidgetId) out.profitWidgetId = s.profitWidgetId;
  const laborManual = Number(s.laborManual);
  if (Number.isFinite(laborManual)) out.laborManual = laborManual;
  if (["$", "/day", "%"].includes(s.laborManualUnit)) out.laborManualUnit = s.laborManualUnit;
  if (Array.isArray(s.expenses)) {
    const expenseRow = (e) => {
      if (!e || typeof e !== "object") return null;
      const out2 = { key: typeof e.key === "string" && e.key ? e.key.slice(0, 40) : `e${Math.random().toString(36).slice(2, 10)}` };
      if (typeof e.label === "string" && e.label.trim()) out2.label = e.label.trim().slice(0, 40);
      /* Negatives allowed through on purpose — a rebate or credit is a real
         line on the sheet (see expenseAmount in components/ProfitTrend/data.js). */
      const amount = Number(e.amount);
      out2.amount = Number.isFinite(amount) ? amount : "";
      out2.unit = ["$", "/day", "%"].includes(e.unit) ? e.unit : "$";
      out2.kind = e.kind === "payroll" ? "payroll" : "opex";
      return out2;
    };
    const rows = s.expenses.map(expenseRow).filter(Boolean).slice(0, 40);
    if (rows.length) out.expenses = rows;
  }
  if (s.goals && typeof s.goals === "object") {
    const goals = {};
    ["revenue", "expenses", "labor", "net"].forEach((key) => {
      const g = s.goals[key];
      if (!g || typeof g !== "object") return;
      const clean = {};
      if (g.on === true) clean.on = true;
      if (g.dir === "min" || g.dir === "max") clean.dir = g.dir;
      if (g.unit === "%" || g.unit === "$") clean.unit = g.unit;
      const warn = Number(g.warn);
      if (g.warn === "" || Number.isFinite(warn)) clean.warn = g.warn === "" ? "" : warn;
      const limit = Number(g.limit);
      if (g.limit === "" || Number.isFinite(limit)) clean.limit = g.limit === "" ? "" : limit;
      if (Object.keys(clean).length) goals[key] = clean;
    });
    if (Object.keys(goals).length) out.goals = goals;
  }
  if (Array.isArray(s.lines)) {
    const lines = s.lines.filter((l) => ["revenue", "expenses", "labor", "net"].includes(l));
    if (lines.length) out.lines = lines;
  }
  if (s.unit === "%") out.unit = "%"; // "$" is the default
  if (["auto", "day", "week", "month"].includes(s.grain)) out.grain = s.grain;
  if (s.workingDaysOnly === true) out.workingDaysOnly = true; // showing every date is the default
  if (s.curve === "linear") out.curve = "linear"; // "monotone" is the default
  if (s.tvRotate === "rotate" || s.tvRotate === "topfit") out.tvRotate = s.tvRotate;
  const tvSize = Number(s.tvRotateSize);
  if (Number.isFinite(tvSize) && tvSize > 0) out.tvRotateSize = Math.min(12, Math.round(tvSize));
  const tvSecs = Number(s.tvRotateSecs);
  if (Number.isFinite(tvSecs) && tvSecs > 0) out.tvRotateSecs = Math.min(120, Math.round(tvSecs));
  if (typeof s.window === "string" && ["follow", "today", "yesterday", "week", "lastWeek", "lastMonth", "month", "quarter", "ytd", "year", "custom"].includes(s.window)) {
    out.window = s.window;
  }
  if (s.customRange && typeof s.customRange === "object") {
    const day = (v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "");
    const from = day(s.customRange.from);
    const to = day(s.customRange.to);
    if (from || to) out.customRange = { ...(from ? { from } : {}), ...(to ? { to } : {}) };
  }
  if (s.avgMode === "total") out.avgMode = "total"; // average is the default
  /* Per-card pay rules on Analytics (Anticipated earnings / Milestones).
     Keyed by workspaceId so multi-workspace cards keep one program each.
     Without this whitelist, Setup edits looked live then vanished on save —
     and the old path wrote Group.bonusConfig so every board shared one setup. */
  if (s.bonusPrograms && typeof s.bonusPrograms === "object") {
    const map = {};
    Object.entries(s.bonusPrograms).forEach(([wsId, prog]) => {
      if (typeof wsId !== "string" || !wsId || !prog || typeof prog !== "object") return;
      map[wsId.slice(0, 40)] = sanitizeProgram(prog);
    });
    if (Object.keys(map).length) out.bonusPrograms = map;
  }
  /* Projected annual compensation — typed Year-1 inputs + live toggles. */
  ["baseSalary", "priorBonuses", "q4Pace", "kickerEarned", "kickerUpside", "ceiling", "targetLow", "targetHigh", "q4Revenue"].forEach((key) => {
    const n = Number(s[key]);
    if (Number.isFinite(n) && n >= 0) out[key] = Math.round(n);
  });
  if (s.useLiveKicker === false) out.useLiveKicker = false;
  /* Opt-in prior quarters (off by default); current quarter live is opt-out. */
  if (s.useLivePrior === false) out.useLivePrior = false;
  if (s.useLiveCurrent === false) out.useLiveCurrent = false;
  /* Legacy flag from older PAC cards — treat as live current quarter. */
  if (s.useLiveQ4 === true) out.useLiveCurrent = true;
  /* Scorekeeper metric that feeds live quarter bonus math. */
  if (typeof s.revenueMetricId === "string" && s.revenueMetricId.trim()) {
    out.revenueMetricId = s.revenueMetricId.trim().slice(0, 120);
  }
  if (typeof s.revenueMetricLabel === "string" && s.revenueMetricLabel.trim()) {
    out.revenueMetricLabel = s.revenueMetricLabel.trim().slice(0, 80);
  }
  if (Array.isArray(s.revenueMetricIds)) {
    const ids = s.revenueMetricIds.map(String).filter(Boolean).slice(0, 24);
    if (ids.length) out.revenueMetricIds = ids;
  }
  if (Array.isArray(s.revenueColumnKeys)) {
    const keys = s.revenueColumnKeys.map(String).filter(Boolean).slice(0, 24);
    if (keys.length) out.revenueColumnKeys = keys;
  }
  /* The viewer's own choice in the card's dropdown. "all" is the default, so
     only a real pick is stored. */
  if (typeof s.cardPick === "string" && s.cardPick && s.cardPick !== "all") out.cardPick = s.cardPick;
  // What sits under the numbers, and which metric it is about. Anything else is
  // dropped — a block name we do not render would be a blank strip on the card.
  if (typeof s.show === "string" && ["ranking", "activity"].includes(s.show)) out.show = s.show;
  if (typeof s.showOn === "string" && s.showOn) out.showOn = s.showOn;
  // Only the OFF state is stored: admins can switch player unless told not to,
  // so an untouched card carries nothing and still behaves correctly.
  if (s.who === false) out.who = false;
  // Which optional card parts are switched off — { partKey: true }. Only true
  // flags survive, so an all-shown card carries no `hide`.
  if (s.hide && typeof s.hide === "object") {
    const hide = Object.keys(s.hide).filter((k) => s.hide[k] === true).reduce((o, k) => ({ ...o, [k]: true }), {});
    if (Object.keys(hide).length) out.hide = hide;
  }
  // Per-part display labels (rename a chip without hiding it). Blank = default.
  if (s.labels && typeof s.labels === "object") {
    const labels = Object.keys(s.labels)
      .filter((k) => typeof s.labels[k] === "string" && s.labels[k].trim())
      .reduce((o, k) => ({ ...o, [k]: s.labels[k].trim().slice(0, 40) }), {});
    if (Object.keys(labels).length) out.labels = labels;
  }
  /* Anticipated earnings multi-ws: combined pay plan (one rule, metrics per
     workspace) — see CombinedEarningsSetup. Also keep display-only merge flags
     for older cards that never migrated. */
  if (s.combinedPay && typeof s.combinedPay === "object") {
    out.combinedPay = sanitizeCombinedPay(s.combinedPay);
  }
  if (s.breakdownSplit === true) out.breakdownSplit = true;
  if (typeof s.breakdownTitle === "string" && s.breakdownTitle.trim()) {
    out.breakdownTitle = s.breakdownTitle.trim().slice(0, 80);
  }
  return Object.keys(out).length ? out : undefined;
};

/**
 * Keep only what we persist — strips react-grid-layout's runtime fields, and
 * carries the block's own settings (`s`) through. It is easy to miss that this
 * REBUILDS each block rather than copying it: any key not named here is gone on
 * the next publish, with no error.
 */
export const sanitizeLayout = (layout) =>
  (Array.isArray(layout) ? layout : [])
    .filter((b) => b && typeof b.i === "string")
    .map((b) => {
      const merged = MERGED[b.i];
      const s = sanitizeSettings(merged ? { ...(b.s || {}), scene: b.s?.scene || merged.scene } : b.s);
      return {
        i: merged ? merged.i : b.i,
        x: Math.max(0, Number(b.x) || 0),
        y: Math.max(0, Number(b.y) || 0),
        w: Math.max(1, Number(b.w) || 1),
        h: Math.max(1, Number(b.h) || 1),
        ...(s ? { s } : {}),
      };
    })
    // A board that had BOTH kotm and kotm-photo would now hold the same id
    // twice, which react-grid-layout cannot key. First one placed wins.
    .filter((b, i, all) => all.findIndex((o) => o.i === b.i) === i);

/**
 * Load the published layout for a workspace from the server.
 * Returns a sanitized layout array, or null when nothing has been published
 * (callers then use the registry default).
 */
export const loadLayout = async (companyId, workspaceId) => {
  if (!workspaceId) return null;
  try {
    const { data } = await GET_WORKSPACE_LAYOUT(workspaceId);
    if (data && Array.isArray(data.layout) && data.layout.length && data.version === VERSION) {
      return sanitizeLayout(data.layout);
    }
  } catch (e) {
    /* offline / server error — callers fall back to the default board */
  }
  return null;
};

/**
 * Publish a layout for the whole workspace — straight to the database.
 */
export const publishLayout = async (companyId, workspaceId, layout) => {
  const clean = sanitizeLayout(layout);
  try {
    await PUT_WORKSPACE_LAYOUT(workspaceId, { version: VERSION, companyId, layout: clean });
    return { ok: true, server: true };
  } catch (e) {
    return { ok: false, server: false };
  }
};

/** Remove the published layout so the workspace returns to the default. */
export const clearLayout = async (companyId, workspaceId) => {
  try {
    await PUT_WORKSPACE_LAYOUT(workspaceId, { version: VERSION, companyId, layout: [] });
  } catch (e) {
    /* offline / server error — nothing cleared */
  }
};
